class CursorMagnetEffect {
  static instance = null;

  constructor(options = {}) {
    this.options = {
      selector: '.sticky-cta.sided',
      radius: 200,
      strength: 0.33,
      activeClass: 'magnet-active',
      useRAF: true,
      ...options
    };

    this.elements = [];
    this.elementsData = [];
    this.initialized = false;
    this.rafId = null;
    this.cursor = { x: 0, y: 0 };
    this.isMouseMoving = false;
    this.lastMouseMoveTime = 0;

    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.updateElementsData = this.updateElementsData.bind(this);
    this.animationLoop = this.animationLoop.bind(this);
    this.throttledUpdate = this.throttle(this.updateElementsTransform.bind(this), 16);
  }

  throttle(callback, delay) {
    let lastCall = 0;
    return function() {
      const now = Date.now();
      if (now - lastCall >= delay) {
        lastCall = now;
        callback.apply(this, arguments);
      }
    };
  }

  static init(options = {}) {
    if (CursorMagnetEffect.instance) {
      return CursorMagnetEffect.instance;
    }

    const instance = new CursorMagnetEffect(options);
    CursorMagnetEffect.instance = instance;
    instance.initialize();

    return instance;
  }

  initialize() {
    const selector = this.options.selector;

    if (typeof selector === 'string') {
      this.elements = Array.from(document.querySelectorAll(selector));
    }
    else if (selector instanceof Element) {
      this.elements = [selector];
    }
    else if (selector instanceof NodeList || Array.isArray(selector)) {
      this.elements = Array.from(selector);
    }

    if (this.elements.length === 0) {
      console.warn(`CursorMagnetEffect: Aucun élément trouvé avec le sélecteur "${this.options.selector}"`);
      return this;
    }

    this.elements.forEach(element => {
      element.style.willChange = 'transform';
      element.style.transform = 'translateZ(0)';
    });

    this.updateElementsData();

    window.addEventListener('mousemove', this.handleMouseMove, { passive: true });
    window.addEventListener('scroll', this.updateElementsData, { passive: true });
    window.addEventListener('resize', this.updateElementsData, { passive: true });

    if (this.options.useRAF) {
      this.rafId = requestAnimationFrame(this.animationLoop);
    }

    this.initialized = true;

    return this;
  }

  updateElementsData() {
    if (this.isUpdatingData) return;

    this.isUpdatingData = true;

    setTimeout(() => {
      this.elementsData = this.elements.map(element => {
        const rect = element.getBoundingClientRect();
        return {
          element,
          center: {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2
          },
          isActive: false,
          lastTransform: { x: 0, y: 0 }
        };
      });

      this.isUpdatingData = false;
    }, 16);
  }

  getDistance(point1, point2) {
    const dx = point1.x - point2.x;
    const dy = point1.y - point2.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  handleMouseMove(event) {
    this.cursor = {
      x: event.clientX,
      y: event.clientY
    };

    this.isMouseMoving = true;
    this.lastMouseMoveTime = Date.now();

    if (!this.options.useRAF) {
      this.throttledUpdate();
    }
  }

  animationLoop() {
    const now = Date.now();
    if (this.isMouseMoving && now - this.lastMouseMoveTime < 100) {
      this.updateElementsTransform();
    } else if (this.isMouseMoving) {
      this.isMouseMoving = false;
      this.resetElementsTransform();
    }

    this.rafId = requestAnimationFrame(this.animationLoop);
  }

  updateElementsTransform() {
    this.elementsData.forEach(data => {
      const distance = this.getDistance(this.cursor, data.center);

      if (distance < this.options.radius) {
        const attraction = (this.options.radius - distance) / this.options.radius * this.options.strength;

        const direction = {
          x: data.center.x - this.cursor.x,
          y: data.center.y - this.cursor.y
        };

        const newPosition = {
          x: direction.x * attraction,
          y: direction.y * attraction
        };

        data.lastTransform = newPosition;

        data.element.style.transition = 'transform 0.2s cubic-bezier(0.33, 1, 0.68, 1)';
        data.element.style.transform = `translate3d(${-newPosition.x}px, ${-newPosition.y}px, 0)`;

        if (!data.isActive) {
          data.element.classList.add(this.options.activeClass);
          data.isActive = true;
        }
      } else if (data.isActive) {
        data.element.style.transition = 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)';
        data.element.style.transform = 'translate3d(0, 0, 0)';

        data.element.classList.remove(this.options.activeClass);
        data.isActive = false;
        data.lastTransform = { x: 0, y: 0 };
      }
    });
  }

  resetElementsTransform() {
    this.elementsData.forEach(data => {
      if (data.isActive) {
        data.element.style.transition = 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)';
        data.element.style.transform = 'translate3d(0, 0, 0)';
        data.element.classList.remove(this.options.activeClass);
        data.isActive = false;
        data.lastTransform = { x: 0, y: 0 };
      }
    });
  }

  addElement(selector) {
    if (!this.initialized) {
      console.warn('CursorMagnetEffect: Initialisez d\'abord l\'effet avec init().');
      return this;
    }

    let newElements = [];

    if (typeof selector === 'string') {
      newElements = Array.from(document.querySelectorAll(selector));
    }
    else if (selector instanceof Element) {
      newElements = [selector];
    }
    else if (selector instanceof NodeList || Array.isArray(selector)) {
      newElements = Array.from(selector);
    }

    for (const newElement of newElements) {
      if (!this.elements.includes(newElement)) {
        newElement.style.willChange = 'transform';
        newElement.style.transform = 'translateZ(0)';

        this.elements.push(newElement);
      }
    }

    this.updateElementsData();

    return this;
  }

  removeElement(selector) {
    if (!this.initialized) return this;

    let elementsToRemove = [];

    if (typeof selector === 'string') {
      elementsToRemove = Array.from(document.querySelectorAll(selector));
    }
    else if (selector instanceof Element) {
      elementsToRemove = [selector];
    }
    else if (selector instanceof NodeList || Array.isArray(selector)) {
      elementsToRemove = Array.from(selector);
    }

    for (const elementToRemove of elementsToRemove) {
      const index = this.elements.indexOf(elementToRemove);

      if (index !== -1) {
        elementToRemove.style.transform = 'none';
        elementToRemove.style.willChange = 'auto';
        elementToRemove.classList.remove(this.options.activeClass);

        this.elements.splice(index, 1);
      }
    }

    this.updateElementsData();

    return this;
  }

  destroy() {
    if (!this.initialized) return;

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('scroll', this.updateElementsData);
    window.removeEventListener('resize', this.updateElementsData);

    this.elements.forEach(element => {
      element.style.transform = 'none';
      element.style.willChange = 'auto';
      element.classList.remove(this.options.activeClass);
    });

    this.elements = [];
    this.elementsData = [];
    this.initialized = false;

    CursorMagnetEffect.instance = null;
  }
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = CursorMagnetEffect;
}